/** Ответ `api/epost-letter` (модуль Epost бэкенда); в SDK его пока нет. */

export const EPOST_LETTER_STATUSES = [
  "transmitting",
  "received",
  "awaiting_funds",
  "checking",
  "checked",
  "check_failed",
  "sending",
  "unknown",
  "submitted",
  "in_print",
  "sent",
  "test_done",
  "lost_unconfirmed",
  "failed",
  "cancelled",
] as const;

export type EpostLetterStatus = (typeof EPOST_LETTER_STATUSES)[number];

export type EpostLetterResource = {
  id: string;
  status: EpostLetterStatus;
  cancellable: boolean;
  /** Отмена отправлена, но центр её ещё не подтвердил (был недоступен). */
  cancel_requested: boolean;
  test: boolean;
  failure_kind: string | null;
  failure_reason: string | null;
  /** Отправить заново имеет смысл: цена, содержимое или типография; не отказ для поддержки. */
  resendable: boolean;
  recipient: {
    name: string;
    line2?: string | null;
    street: string;
    zip: string;
    city: string;
    country: string;
  };
  sender: { name: string; street: string; zip: string; city: string };
  options: {
    color?: boolean;
    duplex?: boolean;
    registered?: string | null;
  } | null;
  cover_letter: boolean;
  page_count: number | null;
  price: {
    net_cents: number | null;
    vat_cents: number | null;
    gross_cents: number | null;
    /** Цена, подтверждённая при отправке, — до ответа центра. */
    confirmed_cents: number | null;
  };
  cancel_until: string | null;
  /** До этого срока письмо ждёт денег, потом центр его отменяет. */
  funds_deadline: string | null;
  status_changed_at: string;
  created_at: string | null;
  source?: {
    type: string;
    id: string;
    document_type: string | null;
    number: string | null;
  } | null;
  /** Все документы конверта — только в карточке письма. */
  documents?: { id: string; document_type: string; number: string }[];
  customer?: { id: string; number: number; name: string } | null;
  created_by?: { id: string; name: string } | null;
  events?: {
    status: EpostLetterStatus;
    failure_reason: string | null;
    occurred_at: string;
  }[];
};

/**
 * Списывается только отправленное письмо; отменённое и неудавшееся — 0 €.
 * Пока центр не назвал цену — та, что подтверждена при отправке.
 */
export const chargedCents = (letter: EpostLetterResource): number | null =>
  ["failed", "check_failed", "cancelled", "test_done"].includes(letter.status)
    ? 0
    : letter.price.gross_cents ?? letter.price.confirmed_cents;

/** Остаток установки из ответа центра; у тестовой установки его нет. */
export type LetterFunds = {
  available_cents: number;
  wait_days: number;
  /** Письма, уже ждущие Guthaben; старый центр поле не присылает. */
  waiting_cents?: number;
} | null;

/**
 * Сколько не хватает на письма; null — хватает или остаток неизвестен.
 * Хватает на сами письма — они уходят сразу, раньше ждущих. Не хватает —
 * встают в очередь к ждущим, и пополнение сначала уйдёт на те.
 */
export const fundsShortfall = (
  funds: LetterFunds | undefined,
  grossCents: number,
): number | null =>
  funds && funds.available_cents < grossCents
    ? grossCents + (funds.waiting_cents ?? 0) - funds.available_cents
    : null;

/**
 * Можно ли ещё отменить: после `cancel_until` письмо в любую минуту уходит
 * в печать — кнопку не показываем (E0a, раздел 15).
 */
export const cancellableNow = (
  letter: EpostLetterResource,
  now: number = Date.now(),
): boolean =>
  letter.cancellable &&
  !letter.cancel_requested &&
  (letter.cancel_until === null || Date.parse(letter.cancel_until) > now);
